import { EditOutlined } from '@ant-design/icons'
import { Button, Row, Space, Table, message } from 'antd'
import { type FC, useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { adminApi } from '~/api'
import { apiUserAuthByAdmin } from '~/api/adminApi'
import { useTableDataSourceKey } from '~/hooks/useTableDataSourceKey'
import { type User } from '~/types/user'
import { ADMIN_AUTH_TOKEN, AUTH_TOKEN } from '~/utils/constants'
import { compareNumber, compareText, showErrors } from '~/utils/helpers'

interface UserListProps {
  users: User[]
  isLoading: boolean
  filterInput: string | null | undefined
}

const UserList: FC<UserListProps> = ({ users, isLoading, filterInput }) => {
  const usersSource = useTableDataSourceKey(users) as User[]
  const disableUserMutation = useMutation({
    mutationFn: async (userId: number) => await adminApi.disableUser(Number(userId)),
    onSuccess: () => {
      message.success('Disabled user successfully')
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: showErrors
  })

  const enableUserMutation = useMutation({
    mutationFn: async (userId: number) => await adminApi.enableuser(Number(userId)),
    onSuccess: () => {
      message.success('Enabled user successfully')
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: showErrors
  })

  const loginMutation = useMutation({
    mutationFn: apiUserAuthByAdmin,
    onSuccess: (token) => {
      const adminToken = localStorage.getItem(AUTH_TOKEN)
      if (adminToken != null) {
        localStorage.setItem(ADMIN_AUTH_TOKEN, adminToken)
      }
      localStorage.setItem(AUTH_TOKEN, token)
      window.location.reload()
    },
    onError: showErrors
  })

  const queryClient = useQueryClient()

  const filterData: User[] = useMemo(() => {
    const filter = filterInput?.toLowerCase()
    if (usersSource == null) {
      return []
    }

    return usersSource.filter(
      (user) =>
        user.name.toLowerCase().includes(filter ?? '') || user.email.toLowerCase().includes(filter ?? '')
    )
  }, [usersSource, filterInput])

  return (
    <Table
      size="small"
      tableLayout="auto"
      dataSource={filterData}
      loading={isLoading}
      footer={() => (
        <Row justify="start">
          <span>Total: {filterData.length} users</span>
        </Row>
      )}
      columns={[
        {
          title: 'ID',
          dataIndex: 'id',
          sorter: (a, b) => compareNumber(a.id, b.id)
        },
        {
          title: 'Name',
          dataIndex: 'name',
          sorter: (a, b) => compareText(a.name, b.name)
        },
        {
          title: 'Email',
          dataIndex: 'email',
          sorter: (a, b) => compareText(a.email, b.email)
        },
        {
          title: 'Softwares',
          dataIndex: 'softwares',
          render: (value, user) => {
            if (value == null) {
              return ''
            }

            return (
              <div>
                {user.softwares.map((i) => (
                  <div key={i.id}>- {i.name}</div>
                ))}
              </div>
            )
          }
        },
        {
          title: 'Role',
          dataIndex: 'role',
          sorter: (a, b) => compareText(a.role, b.role)
        },
        {
          title: 'Actions',
          render: (_, user) => {
            return (
              <Space size="middle">
                <Link to={`edit/${user.id}`}>
                  <Button type="primary" icon={<EditOutlined />} />
                </Link>
                {
                  user.role !== 'admin' && user.disabled_at == null && <>
                    <Button type="primary"
                      onClick={() => {
                        disableUserMutation.mutate(user.id)
                      }}>
                      Disable
                    </Button>
                    <Button type="primary"
                      loading={loginMutation.isPending}
                      onClick={() => {
                        loginMutation.mutate(user.id)
                      }}>
                      Login
                    </Button>
                  </>
                }

                {user.role !== 'admin' && user.disabled_at != null &&
                  <Button type="primary"
                    onClick={() => {
                      enableUserMutation.mutate(user.id)
                    }}>
                    Enable
                  </Button>}
              </Space>
            )
          }
        }
      ]}
    />
  )
}

export default UserList
